import {
	type RewardChange,
	type RewardPosition,
	type RewardScale,
	type RewardTitle,
	type RewardUser,
	currentPositions,
	currentSalaries,
	isRewardEmployee,
} from './buildRewardFunctions';

export type RewardSummary = {
	/** Employees with a full-time salary and a usable scale on their function. */
	withScale: number;
	/** Salary sits on the scale, including either endpoint. */
	withinScale: number;
	/** Salary sits below the start or above the end of the scale. */
	outsideScale: number;
	/**
	 * How much the male average exceeds the female average, as a share of the
	 * male average. Same people as the gender-gap total: a salary and a male or
	 * female gender, whether or not their function has a scale. Absent when
	 * either side is missing or the male average is zero.
	 */
	genderPayGap?: number;
};

function usableScale(
	scale: RewardScale | undefined,
): scale is RewardScale & { startAmount: number; endAmount: number } {
	return (
		!!scale &&
		typeof scale.startAmount === 'number' &&
		typeof scale.endAmount === 'number' &&
		scale.endAmount > scale.startAmount
	);
}

const average = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / values.length;

export function buildRewardSummary({
	users,
	positions,
	titles,
	scales,
	changes,
	now,
	viewerID,
}: {
	users: RewardUser[];
	positions: RewardPosition[];
	titles: RewardTitle[];
	scales: RewardScale[];
	changes: RewardChange[];
	now: Date;
	viewerID?: string;
}): RewardSummary {
	const titlesByID = new Map(titles.map((title) => [title.jobTitleID, title]));
	const scalesByID = new Map(scales.map((scale) => [scale.salaryScaleID, scale]));
	const positionByUser = currentPositions(positions, now);
	const salaryByUser = currentSalaries(changes);
	let withScale = 0;
	let withinScale = 0;
	let outsideScale = 0;
	const male: number[] = [];
	const female: number[] = [];

	for (const user of users) {
		if (!isRewardEmployee(user, now, viewerID)) {
			continue;
		}

		const salary = salaryByUser.get(user.userID);

		if (!salary) {
			continue;
		}

		const jobTitleID = positionByUser.get(user.userID)?.jobTitleID;
		const title = jobTitleID ? titlesByID.get(jobTitleID) : undefined;
		const scale = title?.salaryScaleID ? scalesByID.get(title.salaryScaleID) : undefined;

		if (usableScale(scale)) {
			withScale += 1;

			if (salary.amount >= scale.startAmount && salary.amount <= scale.endAmount) {
				withinScale += 1;
			} else {
				outsideScale += 1;
			}
		}

		if (user.gender === 'MALE') {
			male.push(salary.amount);
		} else if (user.gender === 'FEMALE') {
			female.push(salary.amount);
		}
	}

	const maleAverage = male.length === 0 ? undefined : average(male);
	const genderPayGap =
		maleAverage == null || maleAverage === 0 || female.length === 0
			? undefined
			: (maleAverage - average(female)) / maleAverage;

	return { withScale, withinScale, outsideScale, genderPayGap };
}
